<template>
  <div class="modal fade" id="RechargeModal" tabindex="-1" ref="rechargeModalRef" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content p-15 p-md-40">
        <div class="modal-header d-block ps-0 pe-0 pt-0 pb-15 pb-md-25 border-0">
          <button 
            type="button" 
            class="btn-close position-absolute top-0 end-0 mt-3 me-3" 
            data-bs-dismiss="modal" 
            aria-label="Close" 
            @click="resetForm"
            :disabled="processing">
          </button>
          <h5 class="modal-title fw-bold text-black">Recharger le compte prépayé</h5>
          <p class="text-muted mb-0" v-if="account">
            Compte: {{ account.accountNumber }} | Solde actuel: {{ formatAmount(account.balance) }} {{ account.currencyCode }}
          </p>
          <p class="text-info small mb-0" v-if="account?.creditLimit">
            Limite de crédit autorisée: {{ formatAmount(account.creditLimit) }} {{ account.currencyCode }}
          </p>
        </div>
        
        <div class="modal-body ps-0 pe-0 pb-0 pt-15 pt-md-25">
          <Form ref="rechargeForm" @submit="processRecharge" :validation-schema="rechargeSchema">
            <div class="row">
              
              <!-- Montant à recharger -->
              <div class="col-md-12">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Montant à recharger <span class="text-danger">*</span>
                  </label>
                  
                  <!-- Montants suggérés -->
                  <div class="btn-group mb-3 w-100" role="group">
                    <button 
                      v-for="amount in suggestedAmounts" 
                      :key="amount"
                      type="button" 
                      class="btn btn-outline-primary"
                      @click="setAmount(amount)">
                      {{ formatAmount(amount) }}
                    </button>
                  </div>

                  <Field name="amount" v-slot="{ field, value }">
                    <input 
                      v-bind="field"
                      :value="value"
                      type="number" 
                      step="0.01"
                      class="form-control shadow-none fs-md-15 text-black" 
                      placeholder="Ou entrer un montant personnalisé"/>
                  </Field>
                  <ErrorMessage name="amount" class="text-danger"/>
                  
                  <small class="text-muted" v-if="estimatedBalance !== null">
                    Solde estimé après rechargement: {{ formatAmount(estimatedBalance) }} {{ account?.currencyCode }}
                  </small>
                </div>
              </div>

              <!-- Méthode de paiement -->
              <div class="col-md-12">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Méthode de paiement <span class="text-danger">*</span>
                  </label>
                  <Field name="paymentMethod" v-slot="{ field }">
                    <Multiselect
                      :options="paymentMethods"
                      :searchable="false"
                      track-by="value"
                      label="label"
                      :model-value="field.value"
                      @update:model-value="handlePaymentMethodChange"
                      placeholder="Sélectionner une méthode"
                    />
                  </Field>
                  <ErrorMessage name="paymentMethod" class="text-danger"/>
                </div>
              </div>

              <!-- Information contextuelle selon la méthode -->
              <div class="col-md-12" v-if="selectedPaymentMethod">
                <div :class="['alert', getAlertClass(), 'mb-15', 'mb-sm-20', 'mb-md-25']">
                  <strong>{{ getPaymentMethodTitle() }}</strong>
                  <div class="mt-2" v-html="getPaymentMethodInfo()"></div>
                </div>
              </div>

              <!-- Boutons -->
              <div class="col-md-12">
                <div class="d-flex gap-2">
                  <button
                    class="btn btn-success flex-fill"
                    type="submit"
                    :disabled="processing || !isFormValid">
                    <span v-if="!processing">
                      {{ getSubmitButtonText() }}
                    </span>
                    <span v-else>
                      <span class="spinner-border spinner-border-sm me-2"></span>
                      Traitement en cours...
                    </span>
                  </button>
                  <button
                    type="button"
                    class="btn btn-danger"
                    data-bs-dismiss="modal"
                    @click="resetForm"
                    :disabled="processing">
                    Annuler
                  </button>
                </div>
              </div>

            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { Form, Field, ErrorMessage } from 'vee-validate';
import ApiService from '../../services/ApiService';
import * as Yup from 'yup';
import { error, success, hideModal } from '../../utils/utils';
import Multiselect from '@vueform/multiselect';

export default defineComponent({
  name: "RechargeModal",
  components: { Form, Field, ErrorMessage, Multiselect },
  props: { account: { type: Object, default: null } },
  emits: ['close', 'openmodal'],
  setup(props, { emit }) {
    const rechargeSchema = Yup.object().shape({
      amount: Yup.number()
        .required("Le montant est obligatoire")
        .min(1, "Le montant minimum est de 1 EURO")
        .positive("Le montant doit être positif")
        .max(10000000, "Montant trop élevé"),
      paymentMethod: Yup.string().required("La méthode de paiement est obligatoire")
    });

    const router = useRouter();

    const rechargeForm = ref<any>(null);
    const rechargeModalRef = ref<HTMLElement | null>(null);
    const processing = ref(false);
    const selectedPaymentMethod = ref<any>(null);
    const currentAmount = ref<number | null>(null);

    const suggestedAmounts = ref([5000, 10000, 25000, 50000, 100000]);

    const paymentMethods = ref([
      { value: 'PAYPAL', label: 'PayPal', type: 'online' },
      { value: 'STRIPE', label: 'Stripe (Carte bancaire)', type: 'online' },
      { value: 'KKIAPAY', label: 'KkiaPay (Mobile Money)', type: 'online' },
      { value: 'BANK_TRANSFER', label: 'Virement bancaire', type: 'offline' },
      { value: 'CASH', label: 'Espèces (guichet)', type: 'offline' }
    ]);

    const estimatedBalance = computed(() => {
      if (!props.account || !currentAmount.value) return null;
      return Number(props.account.balance) + Number(currentAmount.value);
    });

    const isFormValid = computed(() => {
      return currentAmount.value && currentAmount.value >= 100 && selectedPaymentMethod.value;
    });

    const formatAmount = (amount: number) => {
      return new Intl.NumberFormat('fr-FR').format(amount);
    };

    const setAmount = (amount: number) => {
      currentAmount.value = amount;
      rechargeForm.value?.setFieldValue('amount', amount);
    };

    const handlePaymentMethodChange = (value: string) => {
      const method = paymentMethods.value.find(m => m.value === value);
      selectedPaymentMethod.value = method || null;
      rechargeForm.value?.setFieldValue('paymentMethod', value);
    };

    const getSubmitButtonText = () => {
      if (!selectedPaymentMethod.value) return 'Procéder';
      if (selectedPaymentMethod.value.type === 'online') return 'Payer maintenant';
      return 'Créer la demande';
    };

        const getAlertClass = () => {
      if (!selectedPaymentMethod.value) return 'alert-info';
      return selectedPaymentMethod.value.type === 'online' ? 'alert-success' : 'alert-warning';
    };

    const getPaymentMethodTitle = () => {
      if (!selectedPaymentMethod.value) return 'Comment ça marche ?';
      return `Rechargement via ${selectedPaymentMethod.value.label}`;
    };

    const getPaymentMethodInfo = () => {
      if (!selectedPaymentMethod.value) return '';

      const method = selectedPaymentMethod.value;

      if (method.type === 'online') {
        return `
          <p class="mb-1"><strong>Traitement ${method.processingTime || 'instantané'}</strong></p>
          <p class="mb-1">Paiement sécurisé via ${method.label}</p>
          <p class="mb-0">Vous serez redirigé vers la page de paiement</p>
        `;
      }

      if (method.value === 'BANK_TRANSFER') {
        return `
          <p class="mb-1"><strong>Traitement : 24-48 heures</strong></p>
          <p class="mb-1">Un mouvement en attente sera créé</p>
          <p class="mb-0">Vous recevrez les coordonnées bancaires par email</p>
        `;
      }

      return `
        <p class="mb-1"><strong>Traitement : immédiat après validation</strong></p>
        <p class="mb-1">Rendez-vous au guichet avec le montant</p>
        <p class="mb-0">Votre compte sera crédité après validation</p>
      `;
    };

    // FONCTION PRINCIPALE : INITIER LE RECHARGEMENT
    const processRecharge = async (values: any) => {
  if (processing.value || !props.account) return;
  processing.value = true;

  try {
    const response = await ApiService.post('/prepaid/recharge/initiate', {
      prepaidAccountId: props.account.id,
      amount: values.amount,
      method: values.paymentMethod
    });

    const { intentId } = response.data.data;

    // MAPPING DES MÉTHODES → NOMS DES ROUTES VUE
    const routeMap: Record<string, string> = {
      PAYPAL: 'RechargePayPal',
      STRIPE: 'RechargeStripe',
      KKIAPAY: 'RechargeKkiaPay'
    };

    const routeName = routeMap[values.paymentMethod];

    if (routeName) {
      success('Redirection vers le paiement...');
      // NAVIGATION INTERNE VUE → PLUS DE PAGE BLANCHE !
      router.push({
        name: routeName,
        query: { intentId }
      });
    } else {
      // Virement ou espèces
      success('Demande enregistrée avec succès !');
      hideModal(rechargeModalRef.value);
      emit('close');
    }
  } catch (err: any) {
    error(err.response?.data?.message || 'Erreur lors du rechargement');
  } finally {
    processing.value = false;
  }
};

    const resetForm = () => {
      rechargeForm.value?.resetForm();
      selectedPaymentMethod.value = null;
      currentAmount.value = null;
      processing.value = false;
    };

    watch(() => props.account, (acc) => {
      if (acc) emit('openmodal', rechargeModalRef.value);
    });

    watch(() => rechargeForm.value?.values?.amount, (val) => {
      currentAmount.value = val ? Number(val) : null;
    });

    return {
      rechargeSchema,
      rechargeForm,
      rechargeModalRef,
      processing,
      selectedPaymentMethod,
      currentAmount,
      suggestedAmounts,
      paymentMethods,
      estimatedBalance,
      isFormValid,
      formatAmount,
      setAmount,
      handlePaymentMethodChange,
      processRecharge,
      resetForm,
      getAlertClass,
      getPaymentMethodTitle,
      getPaymentMethodInfo,
      getSubmitButtonText,
    };
  }
});
</script>

<style scoped>
.btn-group .btn {
  flex: 1;
  font-size: 0.9rem;
  padding: 0.5rem;
}

.btn-group .btn:hover {
  transform: translateY(-2px);
  transition: all 0.2s;
}

.btn-close {
  z-index: 1;
}
</style>